import React, { useReducer, useState } from 'react';
import {
  Box,
  TextField,
  MenuItem,
  Typography,
  Button,
  Stack,
  IconButton,
  Autocomplete,
  Chip,
  Collapse,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import type { ServiceCapability } from '../../../types/backend';
import { Priority, RuleKind } from '../../../types/backend';
import type { Prompt } from '../../../types/backend';
import type { ServiceCapabilityCreateRequest, ServiceCapabilityUpdateRequest } from '../services/serviceOfferingsService';
import type { RuleTemplateListItem } from '../../../types/frontend';
import type { ServiceComponentSummary } from '../../service-administration/services/catalogService';
import { RULE_KIND_LABELS, PRIORITY_LABELS } from '../labels';
import TemplatePicker from './TemplatePicker';
import TemplatePreview from './TemplatePreview';
import ComponentPicker from './ComponentPicker';
import PromptPicker from './PromptPicker';
import PromptPreview from './PromptPreview';

const NEW_CAPABILITY_AGENT_ID = 'automation';

interface EnablementEntry {
  ref: string;
}

interface FormState {
  capability_id: string;
  name: string;
  display_name: string;
  description: string;
  group: string;
  // SIGNAL
  template_ref_context: string;
  template_ref_type: string;
  template_ref_id: string;
  rule_kind: string;
  default_priority: string;
  enrichment_prompt_id: string;
  automation_prompt_id: string;
  // The batch agent that runs the automation prompt; kept from the stored capability.
  automation_agent_id: string;
  // COMPONENTS (repeatable)
  enablement_refs: EnablementEntry[];
  // DEPENDS ON
  depends_on: string[];
}

const EMPTY_FORM: FormState = {
  capability_id: '',
  name: '',
  display_name: '',
  description: '',
  group: '',
  template_ref_context: 'event_management',
  template_ref_type: 'rule_template',
  template_ref_id: '',
  rule_kind: RuleKind.REACTIVE,
  default_priority: Priority.P2,
  enrichment_prompt_id: '',
  automation_prompt_id: '',
  automation_agent_id: '',
  enablement_refs: [],
  depends_on: [],
};

function capabilityToForm(cap: ServiceCapability): FormState {
  const enrichmentRef = cap.prompt_refs?.find((r) => r.role === 'enrichment');
  const automationRef = cap.prompt_refs?.find((r) => r.role === 'automation');

  return {
    capability_id: cap.capability_id,
    name: cap.name,
    display_name: cap.display_name,
    description: cap.description ?? '',
    group: cap.group ?? '',
    template_ref_context: cap.template_ref?.target_context ?? 'event_management',
    template_ref_type: cap.template_ref?.asset_type ?? 'rule_template',
    template_ref_id: cap.template_ref?.asset_id ?? '',
    rule_kind: cap.rule_kind ?? RuleKind.REACTIVE,
    default_priority: cap.default_priority ?? Priority.P2,
    enrichment_prompt_id: enrichmentRef?.asset_id ?? '',
    automation_prompt_id: automationRef?.asset_id ?? '',
    automation_agent_id: cap.automation_agent_id ?? '',
    enablement_refs: (cap.enablement_refs ?? []).map((r) => ({ ref: r.ref })),
    depends_on: cap.depends_on ?? [],
  };
}

function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/-{2,}/g, '-');
}

type FormAction =
  | { type: 'field'; field: keyof Omit<FormState, 'enablement_refs' | 'depends_on'>; value: string }
  | { type: 'addEnablement' }
  | { type: 'removeEnablement'; index: number }
  | { type: 'setEnablement'; index: number; value: string }
  | { type: 'setDependsOn'; value: string[] }
  | { type: 'clearTemplate' };

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'field': {
      const next = { ...state, [action.field]: action.value };
      if (action.field === 'display_name') {
        next.name = slugify(action.value);
        next.capability_id = slugify(action.value);
      }
      return next;
    }
    case 'addEnablement':
      return { ...state, enablement_refs: [...state.enablement_refs, { ref: '' }] };
    case 'removeEnablement':
      return { ...state, enablement_refs: state.enablement_refs.filter((_, i) => i !== action.index) };
    case 'setEnablement': {
      const refs = [...state.enablement_refs];
      refs[action.index] = { ref: action.value };
      return { ...state, enablement_refs: refs };
    }
    case 'setDependsOn':
      return { ...state, depends_on: action.value };
    case 'clearTemplate':
      return {
        ...state,
        template_ref_id: '',
        rule_kind: RuleKind.REACTIVE,
        default_priority: Priority.P2,
        enrichment_prompt_id: '',
        automation_prompt_id: '',
      };
    default:
      return state;
  }
}

interface ValidationErrors {
  display_name?: string | null;
  capability_id?: string | null;
  signal_or_component?: string | null;
}

function validate(form: FormState, isNew: boolean): ValidationErrors {
  const errors: ValidationErrors = {};

  if (!form.display_name.trim()) {
    errors.display_name = 'Capability name is required';
  }

  if (isNew) {
    const derived = slugify(form.display_name.trim());
    if (!derived) {
      errors.capability_id = 'Name must contain at least one letter or number';
    }
  }

  const hasSignal = !!form.template_ref_id.trim();
  const hasComponent = form.enablement_refs.some((r) => r.ref.trim());
  if (!hasSignal && !hasComponent) {
    errors.signal_or_component = 'Add at least one event pattern (Signal) or one Component';
  }

  return errors;
}

interface CapabilityFormProps {
  capability?: ServiceCapability;
  isNew: boolean;
  saving: boolean;
  templates?: RuleTemplateListItem[];
  templatesLoading?: boolean;
  components?: ServiceComponentSummary[];
  componentsLoading?: boolean;
  prompts?: Prompt[];
  promptsLoading?: boolean;
  offeringCapabilities?: ServiceCapability[];
  onSave: (data: ServiceCapabilityCreateRequest | ServiceCapabilityUpdateRequest) => Promise<void>;
  onCancel: () => void;
}

const CapabilityForm: React.FC<CapabilityFormProps> = ({
  capability,
  isNew,
  saving,
  templates = [],
  templatesLoading = false,
  components = [],
  componentsLoading = false,
  prompts = [],
  promptsLoading = false,
  offeringCapabilities = [],
  onSave,
  onCancel,
}) => {
  const [form, dispatch] = useReducer(formReducer, capability ? capabilityToForm(capability) : EMPTY_FORM);
  const [errors, setErrors] = React.useState<ValidationErrors>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showDependsOn, setShowDependsOn] = useState(
    capability ? (capability.depends_on?.length ?? 0) > 0 : false,
  );

  const field = (f: keyof Omit<FormState, 'enablement_refs' | 'depends_on'>) =>
    (e: React.ChangeEvent<HTMLInputElement>) => dispatch({ type: 'field', field: f, value: e.target.value });

  const derivedId = isNew ? slugify(form.display_name) : form.capability_id;
  const hasSignal = !!form.template_ref_id.trim();

  const selectedTemplate = hasSignal
    ? (templates.find((t) => t.template_id === form.template_ref_id) ?? null)
    : null;
  const selectedEnrichmentPrompt = (hasSignal && form.enrichment_prompt_id)
    ? (prompts.find((p) => p.id === form.enrichment_prompt_id) ?? null)
    : null;
  const selectedAutomationPrompt = (hasSignal && form.automation_prompt_id)
    ? (prompts.find((p) => p.id === form.automation_prompt_id) ?? null)
    : null;

  // Other capabilities in this offering for depends_on selection
  const otherCapabilities = offeringCapabilities.filter(
    (c) => c.capability_id !== capability?.capability_id,
  );

  const handleSave = async () => {
    const errs = validate(form, isNew);
    setErrors(errs);
    if (Object.values(errs).some(Boolean)) return;

    setSubmitError(null);
    try {
      const templateRef = form.template_ref_id.trim()
        ? {
            target_context: form.template_ref_context,
            asset_type: form.template_ref_type,
            asset_id: form.template_ref_id,
          }
        : null;

      const promptRefs: { target_context: string; asset_type: string; asset_id: string; role: 'enrichment' | 'automation' }[] = [];
      if (hasSignal && form.enrichment_prompt_id.trim()) {
        promptRefs.push({
          target_context: 'prompt_management',
          asset_type: 'prompt',
          asset_id: form.enrichment_prompt_id.trim(),
          role: 'enrichment',
        });
      }
      if (hasSignal && form.automation_prompt_id.trim()) {
        promptRefs.push({
          target_context: 'prompt_management',
          asset_type: 'prompt',
          asset_id: form.automation_prompt_id.trim(),
          role: 'automation',
        });
      }

      const enablementRefs = form.enablement_refs
        .filter((r) => r.ref.trim())
        .map((r) => ({ provisioner_kind: 'AWS_SC' as const, ref: r.ref.trim() }));

      const dependsOn = form.depends_on.length > 0 ? form.depends_on : undefined;
      // A new automation prompt runs the shipped batch agent until the agent picker (slice C12) replaces this.
      const automationAgentId = hasSignal && form.automation_prompt_id.trim()
        ? form.automation_agent_id || NEW_CAPABILITY_AGENT_ID
        : undefined;

      if (isNew) {
        await onSave({
          capability_id: derivedId,
          name: form.name || derivedId,
          display_name: form.display_name,
          description: form.description || undefined,
          group: form.group || undefined,
          template_ref: templateRef,
          rule_kind: templateRef ? form.rule_kind : null,
          default_priority: templateRef ? form.default_priority : null,
          prompt_refs: promptRefs.length > 0 ? promptRefs : undefined,
          automation_agent_id: automationAgentId,
          enablement_refs: enablementRefs.length > 0 ? enablementRefs : undefined,
          depends_on: dependsOn,
        } as ServiceCapabilityCreateRequest);
      } else {
        await onSave({
          display_name: form.display_name,
          description: form.description || undefined,
          group: form.group || undefined,
          template_ref: templateRef,
          rule_kind: templateRef ? form.rule_kind : null,
          default_priority: templateRef ? form.default_priority : null,
          prompt_refs: promptRefs.length > 0 ? promptRefs : undefined,
          automation_agent_id: automationAgentId,
          enablement_refs: enablementRefs.length > 0 ? enablementRefs : undefined,
          depends_on: dependsOn,
        } as ServiceCapabilityUpdateRequest);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Save failed';
      if (message.toLowerCase().includes('conflict') || message.toLowerCase().includes('already exists')) {
        setErrors((prev) => ({
          ...prev,
          display_name: 'A capability with a similar name already exists — try a more specific name',
        }));
      } else {
        setSubmitError(message);
      }
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {submitError && (
        <Typography color="error" variant="body2">{submitError}</Typography>
      )}

      {/* Core identity */}
      <TextField
        label="Capability name"
        value={form.display_name}
        onChange={field('display_name')}
        error={!!errors.display_name || !!errors.capability_id}
        helperText={
          errors.display_name || errors.capability_id
            ? (errors.display_name ?? errors.capability_id)
            : 'Describe what the customer gets, e.g. "Detects unusual cost spikes"'
        }
        size="small"
        required
      />

      {isNew && (
        <TextField
          label="Capability ID (auto-derived)"
          value={derivedId}
          size="small"
          InputProps={{ readOnly: true }}
          helperText="Derived from the capability name — visible so you can spot collisions before saving"
          sx={{ '& input': { color: 'text.secondary' } }}
        />
      )}

      <TextField
        label="Description"
        value={form.description}
        onChange={field('description')}
        size="small"
        multiline
        rows={2}
      />

      <TextField
        label="Group (optional)"
        value={form.group}
        onChange={field('group')}
        size="small"
        helperText="Grouping tag, e.g. 'Baseline Monitoring'"
      />

      {/* SIGNAL section */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
          Signal — what it watches for
        </Typography>

        <Stack spacing={1.5}>
          <TemplatePicker
            templates={templates}
            value={form.template_ref_id || null}
            onChange={(templateId) => {
              if (templateId) {
                dispatch({ type: 'field', field: 'template_ref_id', value: templateId });
              } else {
                dispatch({ type: 'clearTemplate' });
              }
            }}
            helperText="Pick the rule template this capability watches"
            loading={templatesLoading}
          />

          {selectedTemplate && (
            <TemplatePreview
              templateId={selectedTemplate.template_id}
              owner={selectedTemplate.owner}
            />
          )}

          {hasSignal && (
            <>
              <TextField
                select
                label="Behaviour"
                value={form.rule_kind}
                onChange={field('rule_kind')}
                size="small"
                helperText={form.rule_kind === RuleKind.REACTIVE
                  ? 'Opens a ticket or triggers automation when the event fires'
                  : 'Logs the event for visibility without triggering actions'}
              >
                {Object.values(RuleKind).map((k) => (
                  <MenuItem key={k} value={k}>{RULE_KIND_LABELS[k] ?? k}</MenuItem>
                ))}
              </TextField>

              <TextField
                select
                label="Severity"
                value={form.default_priority}
                onChange={field('default_priority')}
                size="small"
              >
                {Object.values(Priority).map((p) => (
                  <MenuItem key={p} value={p}>{PRIORITY_LABELS[p] ?? p}</MenuItem>
                ))}
              </TextField>

              <PromptPicker
                prompts={prompts}
                value={form.enrichment_prompt_id || null}
                onChange={(promptId) =>
                  dispatch({ type: 'field', field: 'enrichment_prompt_id', value: promptId ?? '' })
                }
                label="Enrichment prompt"
                helperText="Prompt used to analyse and enrich the event"
                loading={promptsLoading}
              />

              {selectedEnrichmentPrompt && (
                <PromptPreview prompt={selectedEnrichmentPrompt} />
              )}

              <PromptPicker
                prompts={prompts}
                value={form.automation_prompt_id || null}
                onChange={(promptId) =>
                  dispatch({ type: 'field', field: 'automation_prompt_id', value: promptId ?? '' })
                }
                label="Automation prompt"
                helperText="Prompt used to drive automated remediation"
                loading={promptsLoading}
              />

              {selectedAutomationPrompt && (
                <PromptPreview prompt={selectedAutomationPrompt} />
              )}
            </>
          )}
        </Stack>
      </Box>

      {/* COMPONENTS section */}
      <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
        <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
          Components — what delivers it in the AWS account
        </Typography>

        {errors.signal_or_component && !form.template_ref_id.trim() && (
          <Typography color="error" variant="caption" sx={{ display: 'block', mb: 1 }}>
            {errors.signal_or_component}
          </Typography>
        )}

        <Stack spacing={1}>
          {form.enablement_refs.map((entry, idx) => (
            <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
              <Box sx={{ flex: 1 }}>
                <ComponentPicker
                  components={components}
                  value={entry.ref || null}
                  onChange={(productName) =>
                    dispatch({ type: 'setEnablement', index: idx, value: productName ?? '' })
                  }
                  helperText="Pick the Service Catalog product this capability provisions"
                  loading={componentsLoading}
                />
              </Box>
              <IconButton
                size="small"
                onClick={() => dispatch({ type: 'removeEnablement', index: idx })}
                sx={{ mt: 0.5 }}
                aria-label="Remove component"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}

          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={() => dispatch({ type: 'addEnablement' })}
            sx={{ alignSelf: 'flex-start' }}
          >
            Add component
          </Button>
        </Stack>
      </Box>

      {/* RUNS AFTER — depends_on (advanced/collapsible) */}
      {otherCapabilities.length > 0 && (
        <Box>
          <Link
            component="button"
            type="button"
            variant="caption"
            onClick={() => setShowDependsOn((v) => !v)}
            sx={{ cursor: 'pointer' }}
          >
            {showDependsOn ? 'Hide' : 'Show'} advanced: Runs after
          </Link>

          <Collapse in={showDependsOn}>
            <Box sx={{ mt: 1, border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block', mb: 1 }}>
                Runs after — dependencies
              </Typography>
              <Autocomplete
                multiple
                size="small"
                options={otherCapabilities}
                getOptionLabel={(o) => o.display_name}
                value={otherCapabilities.filter((c) => form.depends_on.includes(c.capability_id))}
                onChange={(_, selected) =>
                  dispatch({ type: 'setDependsOn', value: selected.map((c) => c.capability_id) })
                }
                isOptionEqualToValue={(a, b) => a.capability_id === b.capability_id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option.capability_id}
                      label={option.display_name}
                      size="small"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Must be ready before this capability provisions"
                    placeholder="Select capabilities…"
                    InputLabelProps={{ shrink: true }}
                    helperText="Other capabilities in this offering that must reach IN_SYNC first"
                  />
                )}
              />
            </Box>
          </Collapse>
        </Box>
      )}

      {errors.signal_or_component && (
        <Typography color="error" variant="caption">
          {errors.signal_or_component}
        </Typography>
      )}

      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1 }}>
        <Button onClick={onCancel} disabled={saving} size="small">Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          size="small"
        >
          {saving ? 'Saving…' : isNew ? 'Add Capability' : 'Save Changes'}
        </Button>
      </Stack>
    </Box>
  );
};

export default CapabilityForm;
